import KlassScopePermission from '@/utils/permissions/KlassScopePermission';
import Permission, { PermissionParams } from '@/utils/permissions/Permission';
import ScheduledLesson from '@/models/ScheduledLesson';
import Klass, { KlassTypeEnum } from '@/models/Klass';
import { VersioningStatusEnum } from '@/enums/VersioningStatus';
import { getAuthorizedUnits } from '../getAuthorizedUnits';

const CONECTA_REQUIRED_FIELDS: (keyof ScheduledLesson)[] = [
  'givenContent',
  'notes',
];

interface ScheduledLessonReportPermissionParams
  extends PermissionParams<ScheduledLesson> {
  klass?: Klass;
}

export default class ScheduledLessonReportPermission extends Permission<ScheduledLesson> {
  klass?: Klass;
  klassScope: KlassScopePermission;

  constructor({ user, model, klass }: ScheduledLessonReportPermissionParams) {
    super({ user, model });
    this.klass = klass;
    this.klassScope = new KlassScopePermission({
      user,
      klass,
      scheduledLesson: model,
    });
  }

  isFromUnitKlass(): boolean {
    return this.klassScope.isFromUnitKlass();
  }

  isTeacherInKlass(): boolean {
    return this.klassScope.isTeacherInKlass();
  }

  canToggleHasDone(): boolean {
    if (!this.user || !this.model) return false;
    if (!this.hasTeacherPrivileges()) return false;
    if (this.hasSuperPrivileges()) return true;
    if (this.hasUnitPrivileges()) return this.isFromUnitKlass();
    return this.isTeacherInKlass() && !this.model.hasDone;
  }

  canCreateOccurrence(): boolean {
    return this.canManage();
  }

  canManage(): boolean {
    if (!this.user) return false;
    if (!this.hasTeacherPrivileges()) return false;
    if (this.hasSuperPrivileges()) return true;
    if (this.hasUnitPrivileges()) return this.isFromUnitKlass();
    return this.isFromUnitKlass() && this.isTeacherInKlass();
  }

  canAcess(): boolean {
    return this.canManage();
  }

  canAccessEnrollment(): boolean {
    if (!this.user || !this.klass) return false;
    if (this.hasUnitPrivileges()) return true;
    return this.hasEnrollments();
  }

  hasEnrollments(klass = this.klass): boolean {
    if (!klass) return false;
    return klass.enrollsCount !== 0;
  }

  canAccessOwnReports(): boolean {
    if (!this.user) return false;
    return this.hasTeacherPrivileges();
  }

  canSeeReportActions(): boolean {
    if (!this.user) return false;
    return this.hasTeacherPrivileges();
  }

  authorizedUnits(): number[] | undefined {
    return getAuthorizedUnits(this.user);
  }

  canEditWhenPublished(): boolean {
    if (!this.user || !this.klass) return false;
    if (!this.hasTeacherPrivileges()) return false;
    return this.model?.lesson?.status === VersioningStatusEnum.PUBLISHED;
  }

  hasRequiredFieldsToFinish(): boolean {
    const model = this.model;
    if (!model || !this.klass) return false;
    if (this.klass.klassType !== KlassTypeEnum.CONECTA) return true;
    return CONECTA_REQUIRED_FIELDS.every(field => Boolean(model[field]));
  }

  canFinishLesson(): boolean {
    if (!this.model || !this.klass) return false;
    if (!this.canToggleHasDone()) return false;
    if (this.model.hasDone) return true;
    return this.hasRequiredFieldsToFinish();
  }

  canEditGivenContent(): boolean {
    if (!this.user || !this.model) return false;
    return this.hasUnitPrivileges() || this.isTeacherInKlass();
  }
}
